import * as Sentry from "@sentry/react";
import { User } from "@sentry/react";
import { isNotNil } from "ramda-adjunct";
import { ERROR_MONITOR_API_KEY, ERROR_MONITOR_DEBUG, ERROR_MONITOR_ENV } from "../env";
import { findGraphqlErrorContext } from "../tools/apolloMiddleware/graphqlErrorContext";

// set true if page uload, to suppress network errors and other errors occured at unload of page
let unloading = false;

/**
 * Initialize error monitor and provide methods for capture errors and exceptions
 */
export const initErrorMonitor = () => {
  if (isNotNil(ERROR_MONITOR_API_KEY)) {
    Sentry.init({
      dsn: ERROR_MONITOR_API_KEY,
      debug: ERROR_MONITOR_DEBUG || false,
      environment: ERROR_MONITOR_ENV || "development",
      // release: version || undefined, // TODO: Add version
      normalizeDepth: 4,
      ignoreErrors: [
        "Non-Error promise rejection captured", // caused by some browser plugins (LastPass, Outlook link check, etc...)
        // Random plugins/extensions
        'Could not load "stats".', // Caused by adblocks which blocking google maps
        "Script error.", // cross-origin script errors with no usable stack trace (can't be scoped by allowUrls)
      ],
      // Only capture errors originating from our React bundle (served under /frontend),
      // not from other (legacy) scripts on the host page picked up by Sentry's global handlers.
      allowUrls: [/\/frontend\//],
      denyUrls: [
        /maps\.googleapis\.com\//i, // block errors in google maps api (caused by adblocks or other plugins)
        // Facebook flakiness
        /graph\.facebook\.com/i,
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i,
      ],
      tracesSampleRate: 0.25,
      beforeSend: (event) => {
        // Ignore error handling when page unloading
        if (unloading) {
          return null;
        }
        return event;
      },
    });
  }
};

globalThis.addEventListener("beforeunload", () => (unloading = true));

/**
 * Set current user as sentry context
 */
export const setUser = (user: User) => {
  Sentry.setUser(user);
};

/**
 * Capture error manually
 *
 * GraphQL failures are captured here from outside the apollo link chain, so the operation name is not at hand.
 * When the link chain has already seen the error, its context is attached, otherwise the event would end up
 * without any hint of which operation failed.
 */
export const captureError = (err: unknown) => {
  if (import.meta.env.MODE === "development") {
    console.error(err);
  }
  const graphqlContext = findGraphqlErrorContext(err);
  Sentry.captureException(err, (scope) => {
    if (graphqlContext) {
      scope.setTag("http.status_code", graphqlContext.httpCode);
      scope.setContext("Graphql", graphqlContext);
    }
    return scope;
  });
};
